import { Spacer } from "@hackthenorth/north";
import { PlusCircleIcon } from "@heroicons/react/24/outline";
import { Button, Checkbox, Select, Table, Text } from "north.js";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";

import { Icon, PageBar } from "src/shared/components";
import Header from "src/shared/components/Header";
// eslint-disable-next-line import/order
import PageWrapper from "src/shared/components/PageWrapper";
import { errorToast, successToast } from "src/shared/components/Toast";
import { UploadIcon } from "src/static/icons";
import { deviceBreakpoints } from "src/theme/deviceBreakpoints";

import { ShiftSearchBar } from "../shared/components/ShiftSearchBar";
import VolunteerShiftModal from "../shared/components/VolunteerShiftModal";
import {
  formatVolunteerShiftDay,
  getVolunteerShiftHourAndMinute,
} from "../shared/utils/date";
import {
  DEFAULT_SHIFT_YEAR,
  parseMaxCountRestriction,
  yearFromDate,
  yearsFromDates,
} from "../shift-schedule/utils";

import BulkDeleteConfirmModal from "./components/BulkDeleteConfirmModal";
import { useDeleteVolunteerShiftMutation } from "./graphql/deleteVolunteerShift.generated";
import { useVolunteerShiftsQuery } from "./graphql/volunteerShifts.generated";
import { useVolunteerShiftSignupsQuery } from "./graphql/volunteerShiftSignups.generated";

const ShiftsPage: React.FC = () => {
  const [page, setPage] = useState<number>(0);
  const [itemsPerPage, setItemsPerPage] = useState<number>(10);
  const [search, setSearch] = useState<string>("");
  const [selectedYear, setSelectedYear] = useState(DEFAULT_SHIFT_YEAR);
  const [selectedShift, setSelectedShift] = useState<number | null>(null);
  const [viewModal, setViewModal] = useState<boolean>(false);
  const [selectedShiftIds, setSelectedShiftIds] = useState<number[]>([]);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const navigate = useNavigate();
  const [deleteShift] = useDeleteVolunteerShiftMutation();

  const { data: volunteerShiftsData, refetch: refetchVolunteerShifts } =
    useVolunteerShiftsQuery({
      variables: {
        where: {},
      },
    });

  const {
    data: volunteerShiftSignups,
    refetch: refetchMyVolunteerShiftSignups,
  } = useVolunteerShiftSignupsQuery({
    variables: {
      where: {},
    },
  });

  const refetchAllShifts = useCallback(() => {
    refetchVolunteerShifts();
    refetchMyVolunteerShiftSignups();
  }, [refetchVolunteerShifts, refetchMyVolunteerShiftSignups]);

  const columns = [
    {
      id: "select",
      name: "",
      hideLabel: true,
    },
    {
      id: "shift",
      name: "shift",
    },
    {
      id: "dateTime",
      name: "date/time",
    },
    {
      id: "location",
      name: "location",
    },
    {
      id: "min",
      name: "preferred min",
    },
    {
      id: "max",
      name: "preferred max",
    },
    {
      id: "registration",
      name: "registration",
    },
    {
      id: "currentlySignedUp",
      name: "currently signed up",
    },
    {
      id: "roles",
      name: "roles",
    },
    {
      id: "actions",
      name: "actions",
      hideLabel: true,
    },
  ];

  const data = useMemo(() => {
    if (!volunteerShiftsData?.volunteerShifts || !volunteerShiftSignups) {
      return [];
    }

    // Chronological by full start datetime (same rule as schedule list).
    // Do not rely on API/creation order.
    const shifts = [...volunteerShiftsData.volunteerShifts].sort((a, b) => {
      const startA = new Date(a.shift?.start_time ?? 0).getTime();
      const startB = new Date(b.shift?.start_time ?? 0).getTime();
      const safeA = Number.isFinite(startA) ? startA : 0;
      const safeB = Number.isFinite(startB) ? startB : 0;
      return safeA - safeB;
    });

    return shifts.map((shift) => {
      const count = volunteerShiftSignups.volunteerShiftSignups.filter(
        (item) => item.shift_id === shift.shift_id
      ).length;

      const startTime = shift.shift?.start_time;
      const endTime = shift.shift?.end_time;

      return {
        shiftId: shift.shift_id,
        startYear: yearFromDate(startTime),
        select: (
          <Checkbox
            checked={selectedShiftIds.includes(shift.shift_id)}
            onChange={() =>
              setSelectedShiftIds((prev) =>
                prev.includes(shift.shift_id)
                  ? prev.filter((id) => id !== shift.shift_id)
                  : [...prev, shift.shift_id]
              )
            }
          />
        ),
        shift: shift.shift?.name ?? "",
        dateTime: `${
          formatVolunteerShiftDay(new Date(startTime ?? 0)) ?? ""
        }, ${getVolunteerShiftHourAndMinute(
          new Date(startTime ?? 0)
        )} - ${getVolunteerShiftHourAndMinute(new Date(endTime ?? 0))}`,
        location: shift.shift?.location ?? "",
        min: shift.preferred_min_signups,
        max: parseMaxCountRestriction(shift.shift?.restrictions),
        registration: (
          <RegistrationPill $open={!!shift.registration_open}>
            {shift.registration_open ? "Open" : "Closed"}
          </RegistrationPill>
        ),
        currentlySignedUp: count,
        roles: shift.category?.name ?? shift.category_name ?? "",
        description: shift.description ?? "",
        shiftLead: shift.organizer_responsible?.name ?? "",
        // plain text for CSV export (React nodes stringify poorly)
        registrationStatus: shift.registration_open ? "Open" : "Closed",
        actions: (
          <Button
            size="sm"
            color="secondary"
            onClick={() => {
              setSelectedShift(shift.shift_id);
              setViewModal(true);
            }}
          >
            <Text tw="font-bold">View</Text>
          </Button>
        ),
      };
    });
  }, [volunteerShiftsData, volunteerShiftSignups, selectedShiftIds]);

  const availableYears = useMemo(
    () =>
      yearsFromDates(
        (volunteerShiftsData?.volunteerShifts ?? []).map(
          (shift) => shift.shift?.start_time
        )
      ),
    [volunteerShiftsData]
  );

  const filteredData = useMemo(() => {
    if (!data) {
      return [];
    }

    const query = search.trim().toLowerCase();

    return data.filter((item) => {
      if (item.startYear !== selectedYear) return false;
      if (!query) return true;

      return (
        item.shift.toLowerCase().includes(query) ||
        item.dateTime.toLowerCase().includes(query) ||
        item.location.toLowerCase().includes(query) ||
        item.max.toString().includes(query) ||
        item.currentlySignedUp.toString().includes(query) ||
        item.roles.toLowerCase().includes(query) ||
        item.registrationStatus.toLowerCase().includes(query)
      );
    });
  }, [data, search, selectedYear]);

  const filteredIds = useMemo(
    () => filteredData.map((item) => item.shiftId),
    [filteredData]
  );

  const allFilteredSelected =
    filteredIds.length > 0 &&
    filteredIds.every((id) => selectedShiftIds.includes(id));

  const selectedForDelete = useMemo(
    () =>
      selectedShiftIds
        .map((id) => filteredData.find((item) => item.shiftId === id))
        .filter((item): item is typeof filteredData[number] => Boolean(item))
        .map((item) => ({ shiftId: item.shiftId, name: item.shift })),
    [filteredData, selectedShiftIds]
  );

  useEffect(() => {
    const visible = new Set(filteredIds);
    setSelectedShiftIds((prev) => {
      const next = prev.filter((id) => visible.has(id));
      return next.length === prev.length ? prev : next;
    });
  }, [filteredIds]);

  const toggleSelectAllFiltered = () => {
    if (allFilteredSelected) {
      setSelectedShiftIds([]);
      return;
    }
    setSelectedShiftIds(filteredIds);
  };

  const handleBulkDelete = async () => {
    if (selectedShiftIds.length === 0) return;
    setDeleting(true);
    const results = await Promise.all(
      selectedShiftIds.map(async (shiftId) => {
        try {
          await deleteShift({ variables: { shiftId } });
          return true;
        } catch {
          return false;
        }
      })
    );
    const deleted = results.filter(Boolean).length;
    const failed = results.length - deleted;
    setDeleting(false);
    setConfirmDeleteOpen(false);
    setSelectedShiftIds([]);
    await refetchAllShifts();

    if (failed === 0) {
      successToast(`Deleted ${deleted} shift${deleted === 1 ? "" : "s"}`);
      return;
    }
    errorToast(
      deleted === 0
        ? "Couldn't delete the selected shifts. Try again."
        : `Deleted ${deleted} shift${
            deleted === 1 ? "" : "s"
          }, but ${failed} failed.`
    );
  };

  useEffect(() => {
    refetchVolunteerShifts();
  }, [refetchVolunteerShifts]);

  useEffect(() => {
    if (viewModal) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [viewModal]);

  const handleDownloadCSV = () => {
    if (!data || data.length === 0) return;

    const headers = Object.keys(data[0]).filter(
      (key) =>
        key !== "actions" &&
        key !== "registration" &&
        key !== "select" &&
        key !== "shiftId" &&
        key !== "startYear"
    );
    const csvRows = [
      headers.join(","),
      ...data.map((row) =>
        headers.map((header) => `"${(row as any)[header]}"`).join(",")
      ),
    ];

    const csvContent = csvRows.join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "volunteer_shifts.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(url);
  };

  return (
    <>
      {selectedShift && (
        <VolunteerShiftModal
          shiftId={selectedShift}
          open={viewModal}
          setOpen={setViewModal}
          onDeleteShift={() => {
            setViewModal(false);
            refetchVolunteerShifts();
          }}
          onChange={() => {
            refetchAllShifts();
          }}
        />
      )}
      <BulkDeleteConfirmModal
        open={confirmDeleteOpen}
        shifts={selectedForDelete}
        deleting={deleting}
        onCancel={() => {
          if (!deleting) setConfirmDeleteOpen(false);
        }}
        onConfirm={handleBulkDelete}
      />

      <Header
        title="Shifts"
        subtitle="View, create, edit, delete, and assign shifts!"
        actionButton={
          <Button
            leadingIcon={
              <Icon name="spon-download" size="14px" color="transparent" />
            }
            color="primary"
            onClick={handleDownloadCSV}
          >
            Download CSV
          </Button>
        }
      />

      <PageWrapper>
        <ControlsContainer>
          <SearchRow>
            <SearchWrap>
              <ShiftSearchBar
                value={search}
                onChange={(value) => {
                  setPage(0);
                  setSearch(value);
                }}
              />
            </SearchWrap>
            <YearSelectWrap>
              <Select
                aria-label="Filter shifts by year"
                size="sm"
                value={String(selectedYear)}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                  const nextYear = Number(e.target.value);
                  if (!Number.isFinite(nextYear)) return;
                  setPage(0);
                  setSelectedYear(nextYear);
                }}
              >
                {availableYears.map((year) => (
                  <Select.Option key={year} value={String(year)}>
                    {year}
                  </Select.Option>
                ))}
              </Select>
            </YearSelectWrap>
          </SearchRow>

          <AddContainer>
            <Button
              leadingIcon={<UploadIcon width={12.8} color="grey" />}
              size="md"
              color="secondary"
              onClick={() => navigate("upload")}
            >
              Upload from CSV
            </Button>
            <Button
              leadingIcon={<PlusCircleIcon width={12.8} color="white" />}
              size="md"
              color="primary"
              onClick={() => navigate("add")}
            >
              Add a shift
            </Button>
            <Button
              size="md"
              color="secondary-danger"
              disabled={selectedShiftIds.length === 0 || deleting}
              onClick={() => setConfirmDeleteOpen(true)}
            >
              Delete selected
              {selectedShiftIds.length > 0
                ? ` (${selectedShiftIds.length})`
                : ""}
            </Button>
          </AddContainer>
        </ControlsContainer>

        {filteredIds.length > 0 && (
          <SelectAllBar>
            <Checkbox
              checked={allFilteredSelected}
              onChange={toggleSelectAllFiltered}
              label={
                allFilteredSelected
                  ? `Deselect all ${filteredIds.length} matching`
                  : `Select all ${filteredIds.length} matching`
              }
            />
          </SelectAllBar>
        )}

        <Spacer height={16} />

        <Table
          displayStyle="striped"
          itemsPerPage={itemsPerPage}
          page={page}
          showFooter
          columns={columns}
          data={filteredData}
        />
        <PageBar
          page={page}
          itemsPerPage={itemsPerPage}
          totalItems={filteredData.length}
          onPageChange={setPage}
          onItemsPerPageChange={setItemsPerPage}
        />
      </PageWrapper>
    </>
  );
};

const AddContainer = styled.div`
  display: flex;
  flex-wrap: wrap;

  > * {
    flex: 1 1 140px;
  }
  flex-direction: row;
  gap: 16px;
`;

const SelectAllBar = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  margin-top: 16px;
`;

const ControlsContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  width: 100%;
  gap: 16px;

  @media (max-width: ${deviceBreakpoints.tablet}px) {
    flex-direction: column;
  }
`;

const SearchRow = styled.div`
  display: flex;
  flex: 1;
  align-items: center;
  gap: 12px;
  min-width: 0;
`;

const SearchWrap = styled.div`
  flex: 1;
  min-width: 0;
`;

const YearSelectWrap = styled.div`
  flex: none;
  min-width: 108px;
`;

const RegistrationPill = styled.div<{ $open: boolean }>`
  border-radius: 27.568px;
  padding: 4px 9.189px;
  text-align: center;
  font-weight: 700;
  font-size: 11.748px;
  width: fit-content;
  background: ${({ $open }) => ($open ? "#d1fae5" : "#e5e7eb")};
  color: ${({ $open }) => ($open ? "#047857" : "#6b7280")};
`;

export default ShiftsPage;
